/**
 * Permissions & roles system.
 *
 * Model:
 *   - businesses/{id}.roles[uid] = roleId (string — references roles/{roleId})
 *   - roles/{roleId} = { name, description, permissions: {...bool}, isSystem }
 *   - users/{uid}.tabAccess = { business: bool, projects: bool }
 *
 * Existing role strings ('owner', 'viewer', 'accountant', 'ops_staff', etc.) are
 * preserved as system role IDs — backwards compatible. Custom roles get UUIDs.
 */

// All permissions in the system. Add here if you need a new one.
export const PERMISSIONS = [
  { key: 'view',                 label: 'View',                 description: 'See the business and its contents' },
  { key: 'edit',                 label: 'Edit',                 description: 'Modify business settings and fields' },
  { key: 'delete',               label: 'Delete',               description: 'Delete the business (destructive)' },
  { key: 'invite',               label: 'Invite users',         description: 'Invite new users to this business' },
  { key: 'manage_users',         label: 'Manage users',         description: 'Grant/revoke access, change roles for existing users' },
  { key: 'view_financials',      label: 'View financials',      description: 'Open ROI Summary and Ledger tab' },
  { key: 'edit_financials',      label: 'Edit financials',      description: 'Post transactions (requires ledger role too)' },
  { key: 'access_internal_os',   label: 'Access Internal OS',   description: 'Open Internal OS sub-app' },
];

export const OWNER_EMAIL = 'nigeltan88@outlook.com';

/**
 * PORTALS — the external apps a user can be granted access to, centrally.
 *
 * This is the single source of truth for portal-level access across the whole
 * NexKoins estate. Each portal reads `users/{uid}.portalAccess[key]` (bool) on
 * login. `key` is the stable identifier a portal checks; never rename one once
 * a portal is wired to it. `url` is informational (shown in the UI).
 *
 * Access is ADDITIVE with each portal's own legacy allowlist during migration:
 * a portal allows a user if they're in its old owner/role list OR
 * portalAccess[key] === true. So granting here never locks anyone out, and
 * revoking here doesn't lock out a legacy-allowlisted owner until that portal
 * has fully cut over. See each portal's auth for the exact rule.
 */
export const PORTALS = [
  { key: 'os-nexkoins', label: 'Portfolio Cockpit', url: 'os.nexkoins.com',
    description: 'GSC performance, leads, ad spend across entities.' },
  { key: 'kng-os',      label: 'KNG OS',            url: 'internal.teamkng.com',
    description: 'Khao Noi Gym staff operations portal.' },
  { key: 'ops-os',      label: 'Ops OS',            url: 'os.nigeltannk.com',
    description: 'nigeltannk provisioning + ops console.' },
  { key: 'ledger',      label: 'Ledger',            url: 'ledger.nexkoins.com',
    description: 'Multi-entity double-entry ledger + statements.' },
  { key: 'unihub',      label: 'UniHub',            url: 'hub.nexkoins.com',
    description: 'Finance dashboard + this access console.' },
];

export const PORTAL_KEYS = new Set(PORTALS.map((p) => p.key));

/** Does this user doc grant access to a given portal? (pure read, no fallback) */
export function hasPortalAccess(userDoc, portalKey) {
  return !!userDoc?.portalAccess?.[portalKey];
}

// System roles — seeded into the roles collection on first load.
// IDs match the existing role strings used in businesses/{id}.roles[uid].
export const SYSTEM_ROLES = [
  {
    id: 'owner',
    name: 'Owner',
    description: 'Full access. Cannot be deleted. Auto-granted to the business creator.',
    isSystem: true,
    permissions: {
      view: true, edit: true, delete: true, invite: true, manage_users: true,
      view_financials: true, edit_financials: true, access_internal_os: true,
    },
  },
  {
    id: 'admin',
    name: 'Admin',
    description: 'Edit + invite. Cannot delete or manage role assignments.',
    isSystem: true,
    permissions: {
      view: true, edit: true, delete: false, invite: true, manage_users: false,
      view_financials: true, edit_financials: false, access_internal_os: true,
    },
  },
  {
    id: 'accountant',
    name: 'Accountant',
    description: 'View + financial access (ROI, Ledger). No other edits.',
    isSystem: true,
    permissions: {
      view: true, edit: false, delete: false, invite: false, manage_users: false,
      view_financials: true, edit_financials: true, access_internal_os: false,
    },
  },
  {
    id: 'viewer',
    name: 'Viewer',
    description: 'Read-only access to the business.',
    isSystem: true,
    permissions: {
      view: true, edit: false, delete: false, invite: false, manage_users: false,
      view_financials: false, edit_financials: false, access_internal_os: false,
    },
  },
  {
    id: 'ops_staff',
    name: 'Ops Staff',
    description: 'View + Internal OS full access.',
    isSystem: true,
    permissions: {
      view: true, edit: false, delete: false, invite: false, manage_users: false,
      view_financials: false, edit_financials: false, access_internal_os: true,
    },
  },
  {
    id: 'content_manager',
    name: 'Content Manager',
    description: 'View + Internal OS (content side).',
    isSystem: true,
    permissions: {
      view: true, edit: false, delete: false, invite: false, manage_users: false,
      view_financials: false, edit_financials: false, access_internal_os: true,
    },
  },
  {
    id: 'biz_dev',
    name: 'Biz Dev',
    description: 'View + edit + Internal OS access.',
    isSystem: true,
    permissions: {
      view: true, edit: true, delete: false, invite: false, manage_users: false,
      view_financials: false, edit_financials: false, access_internal_os: true,
    },
  },
  {
    id: 'trainer',
    name: 'Trainer',
    description: 'View + Internal OS (trainer-facing).',
    isSystem: true,
    permissions: {
      view: true, edit: false, delete: false, invite: false, manage_users: false,
      view_financials: false, edit_financials: false, access_internal_os: true,
    },
  },
];

export const SYSTEM_ROLE_IDS = new Set(SYSTEM_ROLES.map((r) => r.id));

/**
 * Check if the user has a given permission on the business.
 * @param {object} business - Firestore business doc
 * @param {object} user - Firebase auth user
 * @param {object} rolesById - keyed map of all roles (id -> role doc)
 * @param {string} perm - permission key
 */
export function hasPermission(business, user, rolesById, perm) {
  if (!business || !user) return false;
  // Global owner bypass (hardcoded to Kai's email for safety)
  if (user.email === OWNER_EMAIL) return true;
  const roleId = business.roles?.[user.uid];
  if (!roleId) return false;
  const role = rolesById?.[roleId];
  return !!role?.permissions?.[perm];
}

export function getUserRoleForBusiness(business, user) {
  return business?.roles?.[user?.uid] || null;
}

export function getRole(rolesById, roleId) {
  return rolesById?.[roleId] || null;
}

/**
 * Return the list of roleIds the current user can assign when inviting/changing.
 * Owner can assign any. Others can only assign roles with a permission subset of their own.
 */
export function assignableRoleIds(rolesById, currentRoleId) {
  if (!rolesById) return [];
  const ids = Object.keys(rolesById);
  if (currentRoleId === 'owner') return ids;
  const current = rolesById[currentRoleId];
  if (!current) return [];
  return ids.filter((id) => {
    const r = rolesById[id];
    if (!r) return false;
    return Object.keys(r.permissions || {}).every((p) =>
      !r.permissions[p] || current.permissions?.[p]
    );
  });
}

/**
 * Default tab access for a newly-provisioned user.
 */
export const DEFAULT_TAB_ACCESS = {
  business: true,
  projects: true,
};
